import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import Image1 from 'images/solar-guide/image60.png';
import Image from 'next/legacy/image';
import Link from 'next/link';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';

const Page = () => {
  return (
    <article>
      <MetaTags
        title="💳 Purchase & Financing Options "
        description="2023 Home Solar Guide"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '💳 Purchase & Financing Options ',
            href: '/home-solar-guide/purchase-and-financing-options',
          },
        ]}
      />
      <CenteredContentSection>
        <div className="flex justify-between items-center">
          <Link href={'/home-solar-guide/economics-of-home-solar'}>
            ← Economics of Home Solar
          </Link>{' '}
          <Link href={'/home-solar-guide/what-affects-solar-project-viability'}>
            What Affects Solar Project Viability? →
          </Link>
        </div>
        <section id="section1">
          <H3>💳 Purchase & Financing Options </H3>
          <ReadingTime wordCount={1306} />
          <p>
            Think of solar financing like buying and financing a car with the
            added benefit of solar panels saving you money on your energy bills
            now and in the future.
          </p>
          <p>
            There are 4 ways to go solar: you can buy your solar system
            outright, secure a loan to pay for it, choose a leasing program, or
            secure a power purchasing agreement. Whichever option you choose,
            solar panels can lower your price for home energy and put more money
            back in your pocket. Here&apos;s how they compare:
          </p>
          <H4>Cash Purchase</H4>
          <p>
            Homeowners with higher taxes often pay for their solar all up front
            and have the greatest return-on-investment over the lifetime of the
            system. With a cash buy the homeowner can claim all tax credits and
            rebates, avoid financing charges, and directly benefit from the
            savings on their electric bills. We have customers that have
            essentially no electric bills from the day they install solar.
          </p>
          <p>
            In return for their upfront investment in a solar installation,
            homeowners will benefit from a reduced monthly electricity bill. For
            instance, let&apos;s say a solar system costs them $10,000 after all
            incentives and saves them $1,500 per year on their electric bill. To
            calculate the simple payback period — the approximate time for them
            to earn their initial investment back — we divide the cost of the PV
            system by the savings.
          </p>
          <p>Simple Payback Period</p>
          <p className="text-xl font-bold text-gray-800">
            $10,000 (System Cost) <span className="text-lg">/</span> $1,500/year
            (Annual Savings) = 6.7 years
          </p>
          <p>
            The payback period for this system that costs $10,000 and reduces
            the electricity bill by $1,500 per year is 6.7 years.This doesn’t
            account for utility rate increases which would reduce the payback
            period as the savings per year would be increasing.
          </p>
          <p>
            A solar system can last much longer than the duration of its payback
            period. In fact a{' '}
            <strong>
              typical rooftop solar system has a lifetime of about 35 years and
              has a warranty for 25 years.
            </strong>{' '}
            This makes the Solar Cash option the lowest-cost way to maximize
            their total lifetime savings.
          </p>
          <H4>Solar Loans</H4>
          <p>
            There are many, many solar loan options available today with various
            term lengths, rates, and structures.
          </p>
          <p>
            Plenty of financiers offer $0 down loan options where the customer
            does not have to pay anything out of pocket to secure the loan and
            pay for the solar system. Most allow them to finance the full cost
            of their solar panel system with simple fixed monthly payments.
          </p>
          <p>
            Many different types of institutions offer solar loans, from
            traditional banks to utility companies. Solar loans have the same
            basic structure, terms and conditions as other home improvement
            loans. Solar loans offer immediate returns by saving them money on
            their monthly electricity bills right away even as they repay the
            loan monthly.
          </p>
          <p>
            Have you ever taken out a loan for a home renovation project? Solar
            panel loans are similar to home improvement loans that homeowners
            have used for decades to build a deck, upgrade their HVAC system, or
            add a second bathroom to their homes. And like these other types of
            loans, when they borrow money from a lender to finance a solar panel
            system they agree to pay it back plus interest in monthly
            installments over a set loan term.
          </p>
          <p>
            The critical difference between a <strong>solar loan</strong>{' '}
            compared to a traditional home loan is how the Federal Investment
            Tax credit is factored into the payments.
          </p>
          <p>Here is an example of a loan for a $30,000 system:</p>
          <p>
            The homeowner is eligible for a 30% ITC rebate equalling $10,000.
            The amount of the loan is $30,000 so the net cost for the customer
            is $30,000 - $10,000 = <strong>$20,000.</strong> This net cost of
            $20,000 is divided into monthly loan payments. The loan will stay
            configured like this for the first 18 months.
          </p>
          <p>
            Even though the customer spends the first 18 months repaying the NET
            cost of $20,000, they still owe $30,000 gross for the system. The
            $10,000 credited to them from the Federal government makes up the
            difference between the gross and net amounts. Once the homeowner
            receives this payment they can keep it or pay it onto the loan.{' '}
          </p>
          <p>
            The most common decision by customers is applying their Tax Credit
            of $10,000 to the loan as a “prepayment”. This keeps the total
            amount of the loan at the NET amount of $20,000 and thus monthly
            payments stay the same for the remainder of the loan.
          </p>
          <p>
            However, should they choose to pay more than $10,000 down by month
            18 then their monthly payments would reduce at month 19 until the
            end of the loan period. Conversely if they could not get the tax
            credit or decided to keep that tax credit money then their payments
            would increase as they were lent the full $30,000.
          </p>
          <p>
            This structure of loan provides flexibility to the homeowner to
            choose how they want to handle their tax credit without having to
            pay interest and a higher monthly payment for the full system price.
          </p>
          <Image src={Image1} alt="Monthly payment chart" />
          <H4>Solar Lease</H4>
          <p>
            Solar leases are similar to car leases in that they are a form of
            third party ownership (TPO). Under a solar lease this third party
            owner (e.g. solar company) installs solar panels on a homeowner’s
            property and then rents them the panels which will produce a
            guaranteed amount of energy at a predetermined monthly rate.
            Companies calculate this rate based on the estimated annual
            production of their solar system and include this rate in their
            contract. A lease will also have a fixed term length which will
            typically range from 20 to 25 years. At the end of term a homeowner
            can generally choose to either purchase their system outright at the
            market value price,{' '}
            <strong>remove their solar system at no cost,</strong> or{' '}
            <strong>renew their contract</strong> (typically one to 5 years) and
            continue monthly payments.
          </p>
          <p>
            Another important element in a solar lease is an annual escalator;
            these are becoming less common, but if included, will increase your
            monthly payment by a preset rate over your term length (typically
            one to five percent each year).
          </p>
          <p>
            Under a solar lease, you won&apos;t own the solar system, but you
            will benefit from the electricity it generates.
          </p>
          <p>
            You typically save <strong>10 to 30 percent</strong> on electricity
            costs with a solar lease.
          </p>
          <H4>Solar Power Purchase Agreement</H4>
          <p>
            Financing a solar panel system with a power purchase agreement,
            otherwise known as a PPA, is similar to leasing or “renting” a solar
            panel system. Simply put, a solar company or PPA financier covers
            all the costs to buy solar equipment and install it on their roof.
            Though the solar panel system is located on their property the solar
            company owns it and therefore takes care of any necessary
            maintenance.
          </p>
          <p>
            The solar panels generate electricity and power their home enabling
            homeowners to save on their monthly utility bills. In exchange they
            agree to pay the owner of the system (i.e. the PPA financier or
            solar company) a set rate for each kilowatt-hour (kWh) the solar
            panel system generates. In other words, they agree to purchase the
            power of the solar panels, hence the name PPA. This rate is
            typically lower than what their utility company charges for the
            electricity they would otherwise use from the grid.
          </p>
          <p>
            Unlike with solar leases, PPA charges could vary from month to month
            since their bill is based on the production of the solar panel
            system. Because solar panels typically produce more electricity
            during the summer than during the winter, some people experience
            higher PPA payments during the summer months, but more savings on
            utility bills as well. There are balanced budget plans which provide
            a <strong>level payment plan</strong> similar to a lease with some
            providers.
          </p>
          <p>
            Importantly, the majority of residential solar PPAs are $0-down.
            Some companies offer prepaid PPA options if they are interested in
            paying the entirety of the PPA upfront, but this is less common.
          </p>
          <p>
            Yearly “escalators” in the PPA rate are also common but losing
            popularity.
          </p>
        </section>
      </CenteredContentSection>
      <SideCardContent titles={['The Difficulty of Installation']} />
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
    </article>
  );
};

export default Page;
